import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { semanticColor } from "@how/design-system";
import { DISPLAY_LOCALE, LoadingState } from "@how/ui";
import { supabase } from "../../lib/supabase";

/** A church's maintenance mode as `website_offline` reports it (ADR 0079). */
export interface WebsiteOfflineState {
  readonly enabled: boolean;
  readonly message: string;
  readonly backAt: string | null;
  readonly setAt: string | null;
  readonly siteUrl: string | null;
  readonly timeZone: string;
  readonly website: boolean;
}

const str = (value: unknown): string | null => (typeof value === "string" ? value : null);

export function toWebsiteOfflineState(raw: unknown): WebsiteOfflineState {
  const d = (raw ?? {}) as Record<string, unknown>;
  return {
    enabled: d["enabled"] === true,
    message: str(d["message"]) ?? "",
    backAt: str(d["backAt"]),
    setAt: str(d["setAt"]),
    siteUrl: str(d["siteUrl"]),
    timeZone: str(d["timeZone"]) ?? "UTC",
    website: d["website"] === true,
  };
}

/** Words for a refusal from set_website_offline, as DCX support needs them. */
export function supportOfflineErrorMessage(error: { code?: string; message: string }): string {
  if (error.code === "23514") return error.message;
  if (error.code === "42501")
    return "Your support session no longer allows this. It may have ended, or it does not include website.manage.";
  if (error.code === "P0002") return "This church was not found.";
  return "The website's state was not changed. Try again.";
}

/** One line of the church's maintenance-mode history, from the audit trail. */
export interface OfflineAuditRow {
  readonly id: number;
  readonly occurredAt: string;
  readonly action: string;
  readonly actorType: string;
  readonly source: string;
  readonly enabled: boolean | null;
  readonly reason: string | null;
}

const ACTION_WORDS: Record<string, (enabled: boolean | null) => string> = {
  "website.offline_set": (enabled) =>
    enabled === true ? "Taken offline" : enabled === false ? "Put back online" : "Changed",
  "website.bypass_rotated": () => "New bypass link",
};

export function auditWords(row: Pick<OfflineAuditRow, "action" | "enabled">): string {
  return (ACTION_WORDS[row.action] ?? (() => row.action))(row.enabled);
}

function useWebsiteOfflineState(tenantId: string) {
  return useQuery({
    queryKey: ["platform-website-offline", tenantId],
    queryFn: async (): Promise<WebsiteOfflineState> => {
      const { data, error } = await supabase.rpc("website_offline", { p_tenant_id: tenantId });
      if (error) throw error;
      return toWebsiteOfflineState(data);
    },
  });
}

function useOfflineAudit(tenantId: string) {
  return useQuery({
    queryKey: ["platform-website-offline-audit", tenantId],
    queryFn: async (): Promise<OfflineAuditRow[]> => {
      const { data, error } = await supabase
        .from("audit_events")
        .select("id, occurred_at, action, actor_type, source, metadata")
        .eq("tenant_id", tenantId)
        .eq("target_table", "website_offline")
        .order("occurred_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data.map((e) => {
        const metadata = (e.metadata ?? {}) as Record<string, unknown>;
        return {
          id: e.id,
          occurredAt: e.occurred_at,
          action: e.action,
          actorType: e.actor_type,
          source: e.source,
          enabled: typeof metadata["enabled"] === "boolean" ? metadata["enabled"] : null,
          reason: str(metadata["reason"]),
        };
      });
    },
  });
}

export interface WebsiteOfflineCardProps {
  readonly tenantId: string;
  readonly tenantName: string;
}

/**
 * A church's website, offline or online, for DCX support (owner decision of 2026-10-14, ADR 0079). Every platform
 * member sees the state and the history; changing it needs an active read and write support session on this church
 * that includes website.manage, which the church approves (or a second super admin, break-glass). Support says why;
 * the note goes into the church's audit log with the session. The bypass link stays the church's to make, from its
 * own Website settings: support never holds it.
 */
export function WebsiteOfflineCard({ tenantId, tenantName }: WebsiteOfflineCardProps) {
  const queryClient = useQueryClient();
  const state = useWebsiteOfflineState(tenantId);
  const audit = useOfflineAudit(tenantId);
  const canChange = useQuery({
    queryKey: ["authorize", tenantId, "website.manage", "write"],
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase.rpc("authorize_check", {
        p_tenant_id: tenantId,
        p_permission_key: "website.manage",
        p_write: true,
      });
      if (error) throw error;
      return data;
    },
  });
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (state.data !== undefined) {
      setEnabled(state.data.enabled);
      setMessage(state.data.message);
    }
  }, [state.data]);

  const save = useMutation({
    mutationFn: async (): Promise<WebsiteOfflineState> => {
      const { data, error } = await supabase.rpc("set_website_offline", {
        p_tenant_id: tenantId,
        p_enabled: enabled,
        p_message: message.trim(),
        p_reason: reason.trim(),
      });
      if (error) throw new Error(supportOfflineErrorMessage(error));
      return toWebsiteOfflineState(data);
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(["platform-website-offline", tenantId], saved);
      void queryClient.invalidateQueries({
        queryKey: ["platform-website-offline-audit", tenantId],
      });
      setReason("");
      notifications.show({
        color: "teal",
        title: saved.enabled ? "Website taken offline" : "Website put back online",
        message: saved.enabled
          ? `${tenantName}'s visitors see the cover page within about 30 seconds.`
          : `${tenantName}'s website is back for visitors within about 30 seconds.`,
      });
    },
    onError: (error: Error) => {
      notifications.show({ color: "red", title: "Not changed", message: error.message });
    },
  });

  const dirty =
    state.data !== undefined &&
    (enabled !== state.data.enabled || message.trim() !== state.data.message);
  const allowed = canChange.data === true;

  return (
    <Card component="section" aria-labelledby="website-offline-title" data-website-offline>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <Title order={2} size="h4" id="website-offline-title">
            Website: online or offline
          </Title>
          {state.data === undefined ? null : state.data.enabled ? (
            <Badge color={semanticColor("warning")} variant="light">
              Offline
            </Badge>
          ) : (
            <Badge color={semanticColor("success")} variant="light">
              Online
            </Badge>
          )}
        </Group>
        {state.isPending || canChange.isPending ? (
          <LoadingState rows={3} />
        ) : state.isError ? (
          <Text size="sm" c="dimmed">
            Could not read the website&apos;s state.
          </Text>
        ) : (
          <>
            <Text size="sm" c="dimmed">
              Offline, every visitor to{" "}
              {state.data.siteUrl === null ? "the church's website" : state.data.siteUrl} sees a
              cover page with the church&apos;s logo and this message; the staff dashboard and
              member portal stay up. The church makes and shares its own bypass link from Website →
              Settings.
            </Text>
            {!allowed ? (
              <Alert color={semanticColor("info")} variant="light" title="Read-only">
                To change it, open a read and write{" "}
                <Anchor component={Link} to="/support" size="sm">
                  support session
                </Anchor>{" "}
                for this church that includes website.manage. The church approves it, or a second
                super admin in an emergency.
              </Alert>
            ) : null}
            <Switch
              label="Website offline"
              checked={enabled}
              disabled={!allowed}
              onChange={(event) => {
                setEnabled(event.currentTarget.checked);
              }}
            />
            <Textarea
              label="Message on the cover page"
              description="Up to 300 characters; empty means the standard sentence."
              maxLength={300}
              autosize
              minRows={2}
              disabled={!allowed}
              value={message}
              onChange={(event) => {
                setMessage(event.currentTarget.value);
              }}
            />
            {allowed ? (
              <TextInput
                label="Why (the church sees this note)"
                required
                maxLength={300}
                value={reason}
                onChange={(event) => {
                  setReason(event.currentTarget.value);
                }}
              />
            ) : null}
            <Group justify="flex-end">
              <Button
                disabled={!allowed || !dirty || reason.trim() === ""}
                loading={save.isPending}
                onClick={() => {
                  save.mutate();
                }}
              >
                {enabled ? "Take offline" : "Put back online"}
              </Button>
            </Group>
          </>
        )}
        <Title order={3} size="h6" mt="xs">
          Recent changes
        </Title>
        {audit.isPending ? (
          <LoadingState rows={2} />
        ) : audit.isError || audit.data.length === 0 ? (
          <Text size="sm" c="dimmed">
            {audit.isError ? "Your platform role cannot read the audit trail." : "No changes yet."}
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={480}>
            <Table aria-label="Maintenance mode changes">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>When</Table.Th>
                  <Table.Th>What</Table.Th>
                  <Table.Th>By</Table.Th>
                  <Table.Th>Note</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {audit.data.map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td>
                      {new Date(row.occurredAt).toLocaleString(DISPLAY_LOCALE, {
                        day: "numeric",
                        month: "short",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </Table.Td>
                    <Table.Td>{auditWords(row)}</Table.Td>
                    <Table.Td>
                      {row.source === "control_plane" ? "DCX support" : "The church"}
                    </Table.Td>
                    <Table.Td>{row.reason ?? ""}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    </Card>
  );
}
